import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText } from '../../../../../styling/stylingHelper';
import type { AICheckerStats } from '../../../../../services/admin/adminService';
import { StatTile } from '../../AdminStatTile';
import type { TFn } from '../types';

/**
 * Surfaced-issue breakdown — what TYPES of issues turned up in the selected window and their proportion,
 * regardless of whether they were ticked off (uses stable per-email primary-type counts from stats).
 * Mirrors the AI cost card's stacked-bar style; sits to its left at the same level.
 */
function AICheckerIssueBreakdown({ stats, t }: { stats: AICheckerStats; t: TFn }) {
  const types = [
    { label: t('adminTesting.review.issueWrong'), value: stats.issueWrong, bar: 'bg-red-500', dot: 'bg-red-500' },
    { label: t('adminTesting.review.issueRevenue'), value: stats.issueRevenue, bar: 'bg-pink-500', dot: 'bg-pink-500' },
    { label: t('adminTesting.review.issueFactuur'), value: stats.issueFactuur, bar: 'bg-purple-500', dot: 'bg-purple-500' },
    { label: t('adminTesting.review.issueNeedsAttention'), value: stats.issueNeedsAttention, bar: 'bg-orange-500', dot: 'bg-orange-500' },
    { label: t('adminTesting.review.issueNewSupplier'), value: stats.issueNewSupplier, bar: 'bg-sky-500', dot: 'bg-sky-500' },
    { label: t('adminTesting.review.issueQuantity'), value: stats.issueQuantity, bar: 'bg-amber-500', dot: 'bg-amber-500' },
  ];
  const total = types.reduce((n, x) => n + x.value, 0);
  const present = types.filter((x) => x.value > 0);
  const pct = (v: number) => (total > 0 ? (v / total) * 100 : 0);

  return (
    <div>
      <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-2')}>{t('adminTesting.review.issueBreakdownHeading')}</div>
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-4">
        {total === 0 ? (
          <div className={cn(secondaryText, 'text-sm py-6 text-center')}>{t('adminTesting.review.issueBreakdownEmpty')}</div>
        ) : (
          <>
            <div className="mb-3">
              <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{t('adminTesting.review.issueBreakdownTotal')}</div>
              <div className={cn('text-2xl font-bold tabular-nums mt-0.5', primaryText)}>{total}</div>
              <div className={cn(secondaryText, 'text-[10px] mt-0.5')}>{t('adminTesting.review.issueBreakdownSub')}</div>
            </div>
            {/* Stacked composition bar */}
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700 mb-3">
              {present.map((x, i) => (
                <div key={i} className={cn('h-full', x.bar)} style={{ width: `${pct(x.value)}%` }} title={`${x.label}: ${x.value}`} />
              ))}
            </div>
            {/* Legend with counts + share */}
            <div className="space-y-1.5">
              {present.map((x, i) => (
                <div key={i} className="flex items-center justify-between gap-2">
                  <span className={cn('flex items-center gap-1.5 text-xs', secondaryText)}>
                    <span className={cn('inline-block h-2.5 w-2.5 rounded-sm', x.dot)} />
                    {x.label}
                  </span>
                  <span className={cn('text-xs tabular-nums', primaryText)}>{x.value} · {pct(x.value).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function AICheckerStatsPanel({ stats, emailsPerHour, t }: { stats: AICheckerStats; emailsPerHour: number; t: TFn }) {
  const clean = stats.total - stats.withIssues;
  const cleanPct = stats.total > 0 ? (clean / stats.total) * 100 : 100;
  return (
    <div className="space-y-4">
      {/* Overview — context that doesn't need action */}
      <div>
        <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-2')}>{t('adminTesting.review.kpiOverviewHeading')}</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatTile label={t('adminTesting.review.statChecked')} value={String(stats.total)} />
          <StatTile label={t('adminTesting.review.statPerHour')} value={emailsPerHour.toFixed(1)} sub={t('adminTesting.review.statPerHourSub')} />
          <StatTile label={t('adminTesting.review.statAccuracy')} value={`${cleanPct.toFixed(1)}%`} sub={`${clean}/${stats.total}`} tone="good" />
          <StatTile label={t('adminTesting.review.statSafeSkip')} value={String(stats.goodSkip)} />
          <StatTile label={t('adminTesting.review.statKnown')} value={String(stats.known)} sub={t('adminTesting.review.statKnownSub')} tone={stats.known > 0 ? 'warn' : 'neutral'} />
        </div>
      </div>

      {/* Issue-type breakdown */}
      <AICheckerIssueBreakdown stats={stats} t={t} />
    </div>
  );
}
